/**
 * Network Builder — converts ReactFlow nodes/edges into an NSNetwork.
 *
 * Maps React Flow node types to NS components and creates connecting pipes.
 * Components without explicit pipe edges get zero-length connecting pipes.
 */

import type { Node, Edge } from 'reactflow';
import type { PipeSegment, Junction, NSComponent, NSSolverState, SIMPLEConfig } from '../types';
import type { Site } from '../../types';
import { DEFAULT_SIMPLE_CONFIG } from '../types';
import { createPipe } from '../network/nsPipe';
import { propsFromPT_SI } from '../core/fluidAdapter';
import { createJunction } from '../network/nsJunction';
import { createNSSolverState } from '../network/nsNetwork';
import { NSBoundary } from '../components/nsBoundary';
import { NSPump } from '../components/nsPump';
import { NSTurbine } from '../components/nsTurbine';
import { NSBoiler } from '../components/nsBoiler';
import { NSCondenser } from '../components/nsCondenser';
import { NSValve } from '../components/nsValve';
import { NSHeatExchanger } from '../components/nsHeatExchanger';
import { NSSplitter } from '../components/nsSplitter';
import { NSMixer } from '../components/nsMixer';
import { NSEjector } from '../components/nsEjector';
import { NSCompressor } from '../components/nsCompressor';
import {
  NSCustom,
  UnsupportedCustomComponentExecutionError,
} from '../components/nsCustom';
import { NSCheckValve } from '../components/nsCheckValve';
import { NSReliefValve } from '../components/nsReliefValve';
import { NSControlValve } from '../components/nsControlValve';
import { NSPressureRegulator } from '../components/nsPressureRegulator';
import { isAnnotationNodeType } from '../../../lib/sim/graphing/annotationNodeTypes';
import {
  admitFinitePositivePipeInput,
  admitPipeRoughness,
} from '../core/inputAdmission';

/**
 * Resolve a pipe's inclination (radians from horizontal, +uphill along
 * flow) for the NS momentum solver. See the inline note at the
 * `createPipe` call site for the bug this replaces.
 *
 * Resolution order (additive, back-compat):
 *   1. `data.inclinationRad` — already radians, +uphill (Feature #91/#95).
 *   2. derive `asin(clamp((elevationDownstream − elevationUpstream)/length, ±1))`
 *      from geo-derived endpoint elevations.
 *   3. fall back to 0 (flat).
 *
 * Crucially this NEVER treats `data.elevation` (absolute ASL metres) as a
 * slope angle — that was the prior defect.
 */
function deriveInclinationRad(data: Record<string, any>, lengthMeters: number): number {
  const incl = data?.inclinationRad;
  if (typeof incl === 'number' && Number.isFinite(incl)) return incl;
  const up = data?.elevationUpstream;
  const down = data?.elevationDownstream;
  if (
    typeof up === 'number' && Number.isFinite(up) &&
    typeof down === 'number' && Number.isFinite(down) &&
    Number.isFinite(lengthMeters) && lengthMeters > 1e-6
  ) {
    const ratio = Math.max(-1, Math.min(1, (down - up) / lengthMeters));
    return Math.asin(ratio);
  }
  return 0;
}

/**
 * Build an NSSolverState from ReactFlow nodes and edges.
 */
export function buildNSNetwork(
  nodes: Node[],
  edges: Edge[],
  config: SIMPLEConfig = DEFAULT_SIMPLE_CONFIG,
  site?: Site | null,
): NSSolverState {
  // Custom nodes are annotation/package drafts only. Fail before allocating a
  // partial network so no caller can accidentally bypass UI readiness and
  // receive a passthrough or legacy JavaScript-derived result.
  const customNode = nodes.find((node) => node.type === 'custom');
  if (customNode) {
    throw new UnsupportedCustomComponentExecutionError(
      customNode.id,
      typeof customNode.data?.label === 'string' ? customNode.data.label : customNode.id,
      customNode.data?.customComponentPackage,
    );
  }
  const pipes = new Map<string, PipeSegment>();
  const junctions = new Map<string, Junction>();
  const components = new Map<string, NSComponent>();

  // Compute atmospheric pressure from site elevation (barometric formula)
  const siteElevation = site?.elevation ?? 0;
  const P_atm = 101325 * Math.pow(1 - 2.25577e-5 * siteElevation, 5.25588); // Pa
  const ambientTemp_K = ((site?.ambientTemp ?? 20) + 273.15); // K
  const wetBulbTemp_K = ((site?.wetBulbTemp ?? site?.ambientTemp ?? 15) + 273.15); // K

  // Create NS components from ReactFlow nodes
  for (const node of nodes) {
    // G1 — graphing/instrumentation nodes are observers, never NS components.
    if (isAnnotationNodeType(node.type)) continue;
    const comp = createNSComponent(node, P_atm, ambientTemp_K, wetBulbTemp_K);
    if (comp) {
      components.set(node.id, comp);
    }
  }

  // Create pipes and junctions from edges
  for (const edge of edges) {
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);
    if (!sourceNode || !targetNode) continue;

    // Check if this edge connects through a pipe node
    if (sourceNode.type === 'pipe' || targetNode.type === 'pipe') {
      const pipeNode = sourceNode.type === 'pipe' ? sourceNode : targetNode;
      const otherNode = sourceNode.type === 'pipe' ? targetNode : sourceNode;
      const isPipeSource = sourceNode.type === 'pipe'; // pipe is on the source side of this edge

      // Create the pipe if not already created (pipe may appear in multiple edges)
      if (!pipes.has(pipeNode.id)) {
        const nCells = pipeNode.data.nCells ?? undefined;
        const pipeLength = pipeNode.data.length || 50;
        // Browser graph admission: defaults apply only when a value is absent.
        // Explicit zero, NaN, strings and non-physical roughness fail closed
        // with the exact editor field address instead of silently becoming a
        // 0.3 m / 0.05 mm pipe through JavaScript's `||` coercion.
        const diameter = admitFinitePositivePipeInput(
          pipeNode.id,
          `nodes.${pipeNode.id}.data.diameter`,
          pipeNode.data.diameter === undefined ? 0.3 : pipeNode.data.diameter,
          'hydraulic diameter in m',
        );
        const roughnessMm = admitPipeRoughness(
          pipeNode.id,
          `nodes.${pipeNode.id}.data.roughness`,
          pipeNode.data.roughness === undefined ? 0.05 : pipeNode.data.roughness,
          diameter * 1000,
          'mm',
        );
        // Geo correctness fix (#91): `createPipe` arg 5 is the pipe
        // inclination in RADIANS from horizontal (+uphill); the momentum
        // solver uses Math.sin(inclination) for the static-head term.
        //
        // The previous code passed `(elevation || 0) * Math.PI/180`, which
        // WRONGLY interpreted the absolute ASL elevation (metres) as a slope
        // angle in degrees — e.g. a 100 m ASL site became a 100° "slope"
        // (sin = 0.985 ≈ vertical), corrupting the gravity term. Resolution
        // order (additive, back-compat):
        //   1) data.inclinationRad — already radians, +uphill (Feature A/B).
        //   2) derive asin(clamp(Δz/length, ±1)) from endpoint elevations.
        //   3) fall back to 0 (flat) — NOT the elevation-as-degrees path.
        const inclinationRad = deriveInclinationRad(pipeNode.data, pipeLength);
        const pipe = createPipe(
          pipeNode.id,
          diameter,
          pipeLength,
          roughnessMm / 1000, // mm → m
          inclinationRad,
          nCells,
          Math.max(611, (isFinite(pipeNode.data.pressure) ? pipeNode.data.pressure : 101.325) * 1000),
          Math.max(274, Math.min(620, (isFinite(pipeNode.data.inletTemp) ? pipeNode.data.inletTemp : 20) + 273.15)),
        );
        pipes.set(pipeNode.id, pipe);
      }

      // Create junction for the other (non-pipe) node and connect to the pipe face
      const pipe = pipes.get(pipeNode.id)!;
      const junctionId = `j_${otherNode.id}`;
      const rawP = otherNode.data.pressure ?? otherNode.data.inletPressure ?? 101.325;
      const jP = Math.max(611, (isFinite(rawP) ? rawP : 101.325) * 1000);
      const rawT = otherNode.data.currentTemp ?? otherNode.data.temperature ?? 20;
      // Compute enthalpy from (P, T) using IAPWS — proper thermodynamic state
      let jH: number;
      try {
        const initProps = propsFromPT_SI(jP, (isFinite(rawT) ? rawT : 20) + 273.15);
        jH = initProps.h;
      } catch {
        jH = 4182 * Math.max(0, (isFinite(rawT) ? rawT : 20)); // Fallback: cp × T
      }

      // Determine if this is a boundary condition (fixed P) or a component junction
      const isBoundary = otherNode.type === 'boundaryCondition';
      const junctionType = isBoundary ? 'boundary' as const : 'component' as const;

      if (!junctions.has(junctionId)) {
        junctions.set(junctionId, createJunction(
          junctionId, junctionType, [], jP, jH,
          otherNode.id  // All junctions get componentId so their component can update h
        ));
      }

      const junction = junctions.get(junctionId)!;
      if (isPipeSource) {
        // Pipe is source → flow goes from pipe outlet to other node's junction
        junction.connections.push({ pipeId: pipeNode.id, faceIndex: pipe.nCells, direction: 'inflow' });
      } else {
        // Pipe is target → flow goes from other node's junction to pipe inlet
        junction.connections.push({ pipeId: pipeNode.id, faceIndex: 0, direction: 'outflow' });
      }
    } else {
      // Create a zero-length connecting pipe between components
      const pipeId = `pipe_${edge.source}_${edge.target}`;
      const rawP = sourceNode.data.pressure ?? sourceNode.data.inletPressure ?? 101.325;
      const P_init = Math.max(611, (isFinite(rawP) ? rawP : 101.325) * 1000); // kPa → Pa, min=611 Pa
      const rawT = sourceNode.data.currentTemp ?? sourceNode.data.outletTemp ?? sourceNode.data.inletTemp ?? 20;
      const T_init = Math.max(274, Math.min(620, (isFinite(rawT) ? rawT : 20) + 273.15)); // Clamp to IAPWS range
      const pipe = createPipe(pipeId, 0.1, 0.1, 0.00005, 0, 2, P_init, T_init);
      pipes.set(pipeId, pipe);

      // Create junctions at source output and target input
      const srcJunctionId = `j_${edge.source}_out`;
      const tgtJunctionId = `j_${edge.target}_in`;

      // Default enthalpy: liquid water at T_init
      const h_init = 4182 * Math.max(0, (T_init - 273.15));

      if (!junctions.has(srcJunctionId)) {
        junctions.set(srcJunctionId, createJunction(srcJunctionId, 'component', [], P_init, h_init, edge.source));
      }
      if (!junctions.has(tgtJunctionId)) {
        junctions.set(tgtJunctionId, createJunction(tgtJunctionId, 'component', [], P_init, h_init, edge.target));
      }

      // Connect pipe to junctions
      const srcJunction = junctions.get(srcJunctionId)!;
      srcJunction.connections.push({ pipeId, faceIndex: 0, direction: 'outflow' });

      const tgtJunction = junctions.get(tgtJunctionId)!;
      tgtJunction.connections.push({ pipeId, faceIndex: 2, direction: 'inflow' });
    }
  }

  return createNSSolverState(pipes, junctions, components, config);
}

/**
 * Solver-boundary backstop for isentropic efficiency: accept only a finite
 * value in (0, 1]; otherwise fall back to the component default. `d.efficiency
 * || fallback` was unsafe — it let eta > 1 through (non-physical enthalpy below
 * the isentropic point) and silently swapped eta = 0 for the default. The UI
 * (handlePropertyChange) is the primary guard; this protects loaded cycles /
 * presets / programmatic updates that bypass it.
 */
function clampEfficiency(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 1
    ? value
    : fallback;
}

/**
 * Map a ReactFlow node to an NS component.
 */
function createNSComponent(node: Node, P_atm: number, T_amb_K: number, T_wb_K: number): NSComponent | null {
  const d = node.data;

  switch (node.type) {
    case 'reactor':
      return new NSBoiler(node.id, {
        T_out: (d.targetTemp || 280) + 273.15,
        P_out: (d.pressure || 6400) * 1000,
      });

    case 'turbine':
      return new NSTurbine(node.id, {
        pressureRatio: d.pressureRatio || 10,
        efficiency: clampEfficiency(d.efficiency, 0.85),
      });

    case 'pump':
      return new NSPump(node.id, {
        mode: d.simulationMode || 'fixed-flow',
        // PD vs centrifugal selection. Default 'centrifugal' preserves legacy
        // behaviour byte-identically; only takes effect in mode 'curve-based'.
        pumpType: d.pumpType ?? 'centrifugal',
        ratedHead: d.ratedHead || 50,
        ratedFlow: d.maxFlowRate || d.ratedFlow || 50,
        speed: d.speed || 1.0,
        efficiency: clampEfficiency(d.efficiency, 0.8),
        mdot: d.maxFlowRate || 50,
      });

    case 'heatExchanger':
      return new NSHeatExchanger(node.id, {
        hxType: d.hxType || 'counter',
        UA: (d.uValue || 3) * (d.area || 100),
      });

    case 'condenser':
      return new NSCondenser(node.id, {
        P_cond: (d.P_out || 12.35) * 1000, // kPa → Pa
      });

    case 'boundaryCondition':
      return new NSBoundary(node.id, {
        P: d.pressure ? d.pressure * 1000 : undefined,
        T: d.temperature ? d.temperature + 273.15 : undefined,
        mdot: d.massFlow,
      });

    case 'splitter':
      return new NSSplitter(node.id, { split_fraction: d.split_fraction || 0.5 });

    case 'mixer':
      return new NSMixer(node.id);

    case 'ejector': {
      // Wire the motive/suction streams from EjectorData (src/simulation/types.ts:
      // motiveMassFlow/suctionMassFlow [kg/s], motiveTemp/suctionTemp [°C],
      // entrainmentRatio [derived output]). NS-solver units are SI (Pa, J/kg, kg/s).
      //
      // ω source: pass the (ṁ_motive, ṁ_suction) PAIR → the model derives
      //   ω = ṁ_suction/ṁ_motive (omega_mode 1). We deliberately do NOT pass
      //   `entrainment_ratio`: the editor classifies entrainmentRatio as a DERIVED
      //   output (SimulationEditor.tsx) and stateSync writes the computed ω back into
      //   node.data.entrainmentRatio. Pinning it here would suppress that computation.
      //
      // P_motive defaults to the inlet junction (the motive stream IS the inlet), so
      // it is left unset. The suction stream is NOT on the single inlet junction, so
      // its (P_suction, h_suction) MUST be supplied. EjectorData carries no stream
      // pressures; we evaluate the suction enthalpy at site atmospheric pressure
      // (P_atm) — a defensible reference for a low-pressure entrained stream — and pass
      // P_suction = P_atm so enthalpy and pressure are mutually consistent.
      // ENGINEERING ASSUMPTION (flagged): suction stream is at atmospheric pressure
      // when no explicit suction pressure is modelled on the node.
      const ejParams: Record<string, number> = {};
      if (Number.isFinite(d.motiveMassFlow) && d.motiveMassFlow > 0) {
        ejParams.mdot_motive = d.motiveMassFlow; // kg/s
      }
      if (Number.isFinite(d.suctionMassFlow) && d.suctionMassFlow >= 0) {
        ejParams.mdot_suction = d.suctionMassFlow; // kg/s → drives ω = ṁ_s/ṁ_m
      }
      // Suction stream temperature → enthalpy at P_atm (SI), with P_suction pinned to match.
      // We do NOT pass h_motive: the motive stream IS the inlet junction, whose enthalpy
      // is already a thermodynamically consistent propsFromPT_SI lookup at the (correct,
      // possibly high) inlet pressure — overriding it with a P_atm-based value would be
      // less accurate, not more.
      if (Number.isFinite(d.suctionTemp)) {
        try {
          ejParams.h_suction = propsFromPT_SI(P_atm, d.suctionTemp + 273.15).h; // J/kg
          ejParams.P_suction = P_atm; // Pa — consistent with the enthalpy lookup above
        } catch {
          // Property lookup failed (out-of-range T) — leave suction to model defaults.
        }
      }
      return new NSEjector(node.id, ejParams);
    }

    case 'expansionValve': {
      // N2 units fix: ExpansionValveData.pressureDrop is a DESIGN PRESSURE
      // DROP in kPa (see src/simulation/types.ts) — the old `pressureDrop/100`
      // pretended it was a dimensionless K (unit nonsense). A user-specified
      // drop is now applied AS a drop: kPa → Pa via the named conversion, fed
      // to NSValve's fixed-ΔP throttle mode (sign-aware, opposes flow).
      // Without a configured drop, fall back to a plain K = 5 resistance
      // (typical throttle-valve loss coefficient), as before.
      const KPA_TO_PA = 1000;
      const fixedDeltaP =
        Number.isFinite(d.pressureDrop) && d.pressureDrop > 0
          ? d.pressureDrop * KPA_TO_PA
          : undefined;
      return new NSValve(node.id, {
        fixedDeltaP,
        K_fully_open: 5,
      });
    }

    case 'absorber':
      return new NSHeatExchanger(node.id, {
        Q_rate: -(d.coolingCapacity || 60) * 1e6, // MW → W, heat removal
      });

    case 'generator':
      return new NSBoiler(node.id, {
        Q_rate: (d.heatInput || 30) * 1e6, // MW → W
      });

    case 'dataCenter': {
      // R1-A2 M1 — dataCenter is solver-backed in BOTH engines (remap executed).
      // NS: a live Q_rate heat injection. The heat into the loop is the IT
      // (compute) load ONLY — IT electrical power converts ~1:1 to heat at the
      // racks; PUE is electrical overhead metadata (fans/UPS), NEVER extra
      // thermal duty. This mirrors the EO mapping (direct_liquid_cooling
      // it_load_kW) and computeCycleKpis (PUE is a SOLVED output).
      // Param precedence: schema `params.itLoad_MW` (library drops, R1-A2) →
      // legacy flat `computeLoad` → the long-standing 10 MW fallback, so every
      // pre-existing canvas keeps byte-identical behaviour.
      const dcParams = (d.params ?? {}) as Record<string, unknown>;
      const itLoadMW =
        typeof dcParams.itLoad_MW === 'number' && Number.isFinite(dcParams.itLoad_MW) && dcParams.itLoad_MW > 0
          ? dcParams.itLoad_MW
          : (d.computeLoad || 10);
      return new NSHeatExchanger(node.id, {
        Q_rate: itLoadMW * 1e6, // MW → W, heat generated
      });
    }

    case 'coolingTower':
      return new NSCondenser(node.id, {
        P_cond: P_atm, // Adjusted for site elevation
      });

    case 'thermalStorage':
      return new NSHeatExchanger(node.id, { Q_rate: 0 });

    case 'compressor':
      return new NSCompressor(node.id, {
        pressureRatio: d.pressureRatio || 4,
        efficiency: clampEfficiency(d.efficiency, 0.80),
      });

    // Flow Control Valves
    case 'checkValve':
      return new NSCheckValve(node.id, { K_open: d.K_open || 2.5 });
    case 'reliefValve':
      return new NSReliefValve(node.id, { P_set: (d.P_set || 10000) * 1000, capacity: d.capacity || 50 });
    case 'controlValve':
      return new NSControlValve(node.id, { Cv_max: d.Cv_max || 100, opening: d.opening || 0.5, characteristic: d.characteristic || 'linear' });
    case 'pressureRegulator':
      return new NSPressureRegulator(node.id, { P_set: (d.P_set || 500) * 1000 });
    case 'globeValve':
    case 'gateValve':
      return new NSValve(node.id, { K_fully_open: d.K_fully_open || 2.0, opening: d.opening || 1.0 });

    // Flow Elements — all modeled as loss coefficients (K-factor)
    case 'bend':
      return new NSValve(node.id, { K_fully_open: d.K_bend || 0.3 }); // 90° bend ≈ 0.3
    case 'tee':
      return null; // Tee is represented by its multi-edge junction topology.
    case 'reducer':
      return new NSValve(node.id, { K_fully_open: d.K_reducer || 0.1 });
    case 'orifice':
      return new NSValve(node.id, { K_fully_open: d.K_orifice || 2.5 });
    case 'nozzle':
      return new NSValve(node.id, { K_fully_open: d.K_nozzle || 0.05 });
    case 'diffuser':
      return new NSValve(node.id, { K_fully_open: d.K_diffuser || 0.15 });
    case 'manifold':
      return null; // Manifold is represented by its multi-edge junction topology.

    // Storage — modeled as large-volume junctions
    case 'tank':
    case 'accumulator':
    case 'pressurizer':
    case 'deaerator':
      return new NSBoundary(node.id, {
        P: d.pressure ? d.pressure * 1000 : undefined,
        T: d.temperature ? d.temperature + 273.15 : undefined,
      });

    // Electrical — passthrough for fluid network (effect on mechanical components)
    case 'motor':
    case 'electricGenerator':
    case 'transformer':
    case 'electricalLoad':
    case 'battery':
    case 'switch':
    case 'pidController':
    case 'signalSource':
      // These domains have no verified NS fluid residual in this release.
      // The node remains in canvas data, but no fabricated fluid component is
      // inserted into the network.
      return null;

    // Sensors — read-only, no effect on flow
    case 'pressureSensor':
    case 'tempSensor':
    case 'flowMeter':
    case 'levelSensor':
    case 'powerMeter':
      return null; // Read-only observer; no fluid residual or source term.

    // Specialized heat transfer — map to NSBoiler/NSCondenser/NSHeatExchanger
    case 'steamGenerator':
    case 'evaporator':
    case 'superheater':
    case 'economizer':
    case 'reheater':
      return new NSHeatExchanger(node.id, {
        Q_rate: d.Q_rate ? d.Q_rate * 1e6 : undefined,
        UA: d.uValue && d.area ? d.uValue * d.area : undefined,
      });
    case 'fuelRod':
      return new NSBoiler(node.id, { Q_rate: (d.powerOutput || 10) * 1e6 });
    case 'chiller':
      return new NSCondenser(node.id, { P_cond: (d.P_cond || P_atm / 1000) * 1000 });
    case 'dryCooler':
    case 'crahUnit':
      return new NSHeatExchanger(node.id, { Q_rate: -(d.coolingCapacity || 10) * 1e6 });

    case 'custom':
      return new NSCustom(node.id, {
        label: d.label,
        // Legacy bytes are retained for serialization compatibility only.
        legacyCustomLogic: d.legacyCustomLogic ?? d.logic,
        customComponentPackage: d.customComponentPackage,
        properties: d.customProperties,
      });

    case 'pipe':
      return null; // Pipes are NS pipe segments, not components

    case 'junction':
      // Junction nodes act as boundary conditions if P/T are specified
      if (d.pressure != null || d.temperature != null) {
        return new NSBoundary(node.id, {
          P: d.pressure ? d.pressure * 1000 : undefined,
          T: d.temperature ? d.temperature + 273.15 : undefined,
          mdot: d.massFlow,
        });
      }
      return null; // Free junction — state determined by solver

    default:
      throw new Error(
        `NS network cannot execute unsupported component type "${String(node.type)}" (id="${node.id}").`,
      );
  }
}
